<template>
  <div class="forgot-password-form" :class="`size-${inputSize}`">
    <el-form
      :model="form"
      :rules="rules"
      ref="forgotForm"
      label-width="0"
      class="forgot-form"
      @submit.native.prevent
    >
      <el-form-item prop="email">
        <el-input
          v-model="form.email"
          :placeholder="$t('validation.emailRequired')"
          :size="inputSize"
          prefix-icon="el-icon-message"
        ></el-input>
      </el-form-item>

      <el-form-item prop="emailCode">
        <div class="email-code-row">
          <el-input
            v-model="form.emailCode"
            :placeholder="$t('auth.emailCode')"
            :size="inputSize"
            class="email-code-input"
            prefix-icon="el-icon-key"
            maxlength="6"
          ></el-input>
          <el-button
            plain
            :size="inputSize"
            class="send-code-btn"
            :loading="sendCodeLoading"
            :disabled="emailCodeCountdown > 0"
            @click="handleSendResetCode"
          >
            {{
              sendCodeLoading
                ? $t('auth.sendingCode')
                : emailCodeCountdown > 0
                  ? `${emailCodeCountdown}s`
                  : $t('auth.sendEmailCode')
            }}
          </el-button>
        </div>
      </el-form-item>

      <el-form-item prop="password">
        <el-input
          v-model="form.password"
          :type="pwdType"
          :placeholder="$t('auth.newPassword')"
          :size="inputSize"
          prefix-icon="el-icon-lock"
        >
          <i
            slot="suffix"
            :class="pwdType === 'password' ? 'el-icon-view' : 'el-icon-hide'"
            class="pwd-toggle"
            @click="pwdType = pwdType === 'password' ? 'text' : 'password'"
          ></i>
        </el-input>
      </el-form-item>

      <el-form-item prop="confirmPassword">
        <el-input
          v-model="form.confirmPassword"
          :type="pwdType"
          :placeholder="$t('auth.confirmPassword')"
          :size="inputSize"
          prefix-icon="el-icon-lock"
          @keyup.enter.native="handleSubmit"
        ></el-input>
      </el-form-item>

      <el-form-item class="submit-item">
        <el-button
          type="primary"
          :size="inputSize"
          class="submit-btn"
          :loading="submitting"
          @click="handleSubmit"
        >
          {{ submitting ? $t('auth.resettingPassword') : $t('auth.resetPassword') }}
        </el-button>
      </el-form-item>
    </el-form>

    <div class="form-footer">
      <el-button type="text" class="back-btn" @click="$emit('back')">
        <i class="el-icon-arrow-left"></i> {{ $t('auth.backToLogin') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { sendResetPasswordEmailCode, resetPasswordByEmail } from '@/api/login'

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const CODE_COUNTDOWN_SECONDS = 90

export default {
  name: 'ForgotPasswordForm',
  props: {
    inputSize: {
      type: String,
      default: 'medium'
    },
    initialEmail: {
      type: String,
      default: ''
    }
  },
  data() {
    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('validation.emailRequired')))
      } else if (!EMAIL_REGEX.test(value)) {
        callback(new Error(this.$t('validation.validEmailRequired')))
      } else {
        callback()
      }
    }
    const validatePass = (rule, value, callback) => {
      if (value.length < 3) {
        callback(new Error(this.$t('validation.passwordMinLength')))
      } else {
        callback()
      }
    }
    return {
      submitting: false,
      sendCodeLoading: false,
      emailCodeCountdown: 0,
      emailCodeTimer: null,
      pwdType: 'password',
      form: {
        email: this.initialEmail || '',
        emailCode: '',
        password: '',
        confirmPassword: ''
      },
      rules: {
        email: [{ required: true, trigger: 'blur', validator: validateEmail }],
        emailCode: [
          { required: true, message: this.$t('validation.emailCodeRequired'), trigger: 'blur' },
          { pattern: /^\d{6}$/, message: this.$t('validation.emailCodeLength'), trigger: 'blur' }
        ],
        password: [{ required: true, trigger: 'blur', validator: validatePass }],
        confirmPassword: [
          { required: true, message: this.$t('validation.confirmPasswordRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.form.password) {
                callback(new Error(this.$t('validation.passwordNotMatch')))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  beforeDestroy() {
    this.clearEmailCodeTimer()
  },
  methods: {
    clearEmailCodeTimer() {
      if (this.emailCodeTimer) {
        clearInterval(this.emailCodeTimer)
        this.emailCodeTimer = null
      }
    },
    startEmailCodeCountdown() {
      this.clearEmailCodeTimer()
      this.emailCodeCountdown = CODE_COUNTDOWN_SECONDS
      this.emailCodeTimer = setInterval(() => {
        if (this.emailCodeCountdown <= 1) {
          this.emailCodeCountdown = 0
          this.clearEmailCodeTimer()
        } else {
          this.emailCodeCountdown -= 1
        }
      }, 1000)
    },
    handleSendResetCode() {
      const email = this.form.email.trim()
      if (!email) {
        this.$message.warning(this.$t('validation.emailRequired'))
        return
      }
      if (!EMAIL_REGEX.test(email)) {
        this.$message.warning(this.$t('validation.validEmailRequired'))
        return
      }
      if (this.emailCodeCountdown > 0 || this.sendCodeLoading) {
        return
      }
      this.sendCodeLoading = true
      sendResetPasswordEmailCode(email).then(res => {
        this.sendCodeLoading = false
        const msg = (res && res.message) || this.$t('tip.resetEmailCodeSent')
        this.$message.success(msg)
        this.startEmailCodeCountdown()
      }).catch(() => {
        this.sendCodeLoading = false
      })
    },
    handleSubmit() {
      this.$refs.forgotForm.validate(valid => {
        if (!valid) return
        this.submitting = true
        resetPasswordByEmail({
          email: this.form.email.trim(),
          password: this.form.password,
          emailCode: this.form.emailCode.trim()
        }).then(res => {
          this.submitting = false
          const msg = (res && res.message) || this.$t('tip.resetPasswordSuccess')
          this.$message.success(msg)
          this.$emit('success', this.form.email.trim())
        }).catch(() => {
          this.submitting = false
        })
      })
    },
    reset() {
      if (this.$refs.forgotForm) {
        this.$refs.forgotForm.resetFields()
      }
      this.clearEmailCodeTimer()
      this.emailCodeCountdown = 0
      this.pwdType = 'password'
    }
  }
}
</script>

<style scoped lang="scss">
$brand: #DC143C;
$brand-dark: #B22222;
$text-muted: #909399;
$border: #ebeef5;

.forgot-password-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 28px 20px;

  .forgot-form {
    flex: 1;
  }

  ::v-deep .el-form-item {
    margin-bottom: 16px;
  }

  ::v-deep .submit-item {
    margin-bottom: 0;
    margin-top: 4px;
  }

  ::v-deep .el-input__inner {
    border-radius: 6px;
    height: 40px;
    line-height: 40px;
    border-color: $border;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
      border-color: $brand;
      box-shadow: 0 0 0 2px rgba(220, 20, 60, 0.1);
    }
  }

  ::v-deep .el-input__prefix {
    color: $text-muted;
  }

  .email-code-row {
    display: flex;
    gap: 10px;
    align-items: stretch;

    .email-code-input {
      flex: 1;
      min-width: 0;
    }

    .send-code-btn {
      flex-shrink: 0;
      min-width: 110px;
      height: 40px;
      padding: 0 14px;
      border-radius: 6px;
      color: $brand;
      border-color: rgba(220, 20, 60, 0.45);
      background: #fff;
      font-size: 13px;

      &:hover,
      &:focus {
        color: $brand;
        border-color: $brand;
        background: rgba(220, 20, 60, 0.06);
      }

      &.is-disabled {
        color: $text-muted;
        border-color: $border;
        background: #fafafa;
      }
    }
  }

  .pwd-toggle {
    cursor: pointer;
    color: $text-muted;
    line-height: 40px;

    &:hover {
      color: $brand;
    }
  }

  .submit-btn {
    width: 100%;
    height: 42px;
    font-size: 15px;
    font-weight: 500;
    border-radius: 6px;
    border: none;
    background: linear-gradient(135deg, $brand 0%, $brand-dark 100%);

    &:hover,
    &:focus {
      opacity: 0.92;
      background: linear-gradient(135deg, $brand 0%, $brand-dark 100%);
    }
  }

  .form-footer {
    text-align: center;
    padding-top: 14px;
    margin-top: 8px;
    border-top: 1px solid $border;

    .back-btn {
      color: $text-muted;
      font-size: 13px;
      padding: 4px 8px;

      i {
        margin-right: 2px;
      }

      &:hover {
        color: $brand;
      }
    }
  }

  // 弹窗大尺寸
  &.size-large {
    padding: 0 30px 24px;

    ::v-deep .el-input__inner {
      height: 48px;
      line-height: 48px;
      border-radius: 8px;
      padding-left: 45px;
    }

    ::v-deep .el-input__prefix {
      left: 15px;

      i {
        font-size: 18px;
      }
    }

    .email-code-row .send-code-btn {
      height: 48px;
      min-width: 118px;
      border-radius: 8px;
      font-size: 14px;
    }

    .pwd-toggle {
      line-height: 48px;
    }

    .submit-btn {
      height: 48px;
      border-radius: 8px;
      font-size: 16px;
    }

    ::v-deep .el-form-item {
      margin-bottom: 20px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ForgotPasswordForm.vue